import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { subscribeNewsletter } from '../services/api';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    const isValid = /[^@\s]+@[^@\s]+\.[^@\s]+/.test(trimmed);
    if (!isValid) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      toast.error('Please enter a valid email address.');
      return;
    }

    try {
      setSubmitting(true);
      setStatus('loading');
      setMessage('');
      const res = await subscribeNewsletter(trimmed);
      setStatus('success');
      setMessage(res?.message || 'Subscribed successfully!');
      toast.success(res?.message || 'Subscribed successfully!');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(err?.message || 'Failed to subscribe. Please try again.');
      toast.error(err?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative bg-gradient-to-r from-teal-600 to-teal-700 py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Side - Interior Image */}
          <div className="relative">
            <div className="aspect-[4/3] w-full rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Modern interior with orange chair"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-8 h-8 bg-white opacity-20 rounded-full animate-bounce"></div>
            <div className="absolute -bottom-6 -right-6 w-6 h-6 bg-white opacity-30 rounded-full animate-pulse"></div>
          </div>

          {/* Right Side - Newsletter Content */}
          <div className="text-center lg:text-left">
            {/* Email Icon */}
            <div className="flex justify-center lg:justify-start mb-6">
              <div className="bg-white bg-opacity-20 p-4 rounded-full">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>

            {/* Main Text */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              SUBSCRIBE TO OUR<br />
              NEWSLETTER
            </h2>
            
            <p className="text-lg text-white opacity-90 mb-8">
              Get the latest updates on new arrivals, offers, and interior design inspiration.
            </p>

            {/* Email Form */}
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto lg:mx-0">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 px-6 py-4 rounded-full text-gray-900 placeholder-gray-500 border-none focus:ring-2 focus:ring-white focus:outline-none disabled:opacity-70"
                required
                disabled={submitting}
              />
              <button
                type="submit"
                disabled={submitting}
                className="bg-black hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full font-medium transition-colors duration-300 whitespace-nowrap"
              >
                {submitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>

            {!!message && (
              <p
                className={`mt-4 text-sm ${
                  status === 'success' ? 'text-emerald-100' : status === 'error' ? 'text-red-100' : 'text-white/80'
                }`}
              >
                {message}
              </p>
            )}

            {/* Additional Info */}
            <p className="text-sm text-white opacity-70 mt-4">
              By subscribing, you agree to receive marketing emails. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>

      {/* Background Pattern */}
      <div className="pointer-events-none absolute inset-0 bg-teal-800 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25px 25px, rgba(255,255,255,0.1) 2px, transparent 2px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
